import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { Loading } from "go-core/components/Loading";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { BillingCustomerApi, UpdateBillingDateApi } from "../../../../../../services/Api/Admin/adminTypes";

interface Props {
	show: boolean;
	onHide: () => void;
	handleRefresh: () => void;
	getBillingCustomer: (organizationId: number) => Promise<BillingCustomerApi>;
	updateBillingDate: (organizationId: number, data: UpdateBillingDateApi) => Promise<BillingCustomerApi>;
	organizationId: number;
}

const OrganizationUpdateBillingDateModalForm = ({
	show,
	onHide,
	handleRefresh,
	updateBillingDate,
	getBillingCustomer,
	organizationId,
}: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState(false);
	const form = useForm<UpdateBillingDateApi>({
		criteriaMode: "all",
	});
	const {
		handleSubmit,
		setError,
		formState,
		control,
		formState: { errors },
		setValue,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const [customerData, setCustomerData] = useState<BillingCustomerApi | undefined>();

	useEffect(() => {
		handleFetchCustomerData();
	}, []);

	const handleFetchCustomerData = async () => {
		try {
			const res = await getBillingCustomer(organizationId);
			setCustomerData(res);
			setValue("billing_day", res.billing_day);
			setValue("billing_month", res.billing_month);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			await updateBillingDate(organizationId, data);
			addSuccessFlash(t("lib:common.flash.completed"));
			onHide();
			handleRefresh();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} loading={loading}>
			<Modal show={show} onHide={onHide}>
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.organization.action.update_billing_date.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{!customerData ? (
						<Loading />
					) : (
						<>
							<FormNumberInput
								errors={errors}
								name="billing_day"
								control={control}
								suffix=""
								allowNegative={false}
								label={t("lib:go_report.chart.aggregates.DAY_OF_MONTH")}
							/>
							<FormNumberInput
								errors={errors}
								name="billing_month"
								control={control}
								suffix=""
								allowNegative={false}
								label={t("lib:go_report.chart.aggregates.MONTH")}
							/>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.save")}
					</ButtonLoading>
					<Button onClick={onHide} variant="light">
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</Modal>
		</FormDirty>
	);
};

export default OrganizationUpdateBillingDateModalForm;
